import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Estado da navegação compartilhado entre a barra azul do topo (botão ☰) e o menu.
 *
 * - computador (≥ 1200 px): barra lateral fixa, que pode ser recolhida para só ícones;
 * - tablet (768–1199 px): gaveta que abre por cima da tela (o operador não perde largura);
 * - celular (< 768 px): gaveta em tela cheia, aberta pelo ☰ da barra do topo (sem barra embaixo,
 *   que ficava por cima dos botões de salvar das telas).
 */

export const BREAKPOINTS = { tablet: 768, desktop: 1200 }
const COLLAPSED_KEY = 'ps-nav-collapsed'

const modeFor = (width) =>
  width >= BREAKPOINTS.desktop ? 'sidebar' : width >= BREAKPOINTS.tablet ? 'drawer' : 'mobile'

const readCollapsed = () => {
  try {
    const value = window.localStorage.getItem(COLLAPSED_KEY)
    if (value !== null) return value === '1'
  } catch (e) {
    // sem localStorage: segue o padrão
  }
  // tela de 1200 a 1439 px começa só com ícones para sobrar espaço para o conteúdo
  return window.innerWidth < 1440
}

const NavigationContext = createContext({
  mode: 'sidebar',
  collapsed: false,
  drawerOpen: false,
  toggle: () => {},
  openDrawer: () => {},
  closeDrawer: () => {},
  setCollapsed: () => {},
  focusSearch: 0,
  trail: [],
  setTrail: () => {},
  entries: [],
  setEntries: () => {},
})

export const NavigationProvider = ({ children, forceMode, forceCollapsed, forceDrawerOpen }) => {
  const [width, setWidth] = useState(() => window.innerWidth)
  const [collapsed, setCollapsedState] = useState(() =>
    forceCollapsed !== undefined ? forceCollapsed : readCollapsed()
  )
  const [drawerOpen, setDrawerOpen] = useState(!!forceDrawerOpen)
  const [focusSearch, setFocusSearch] = useState(0)
  // caminho da tela atual (barra do topo) e destinos da busca, preenchidos pelo menu
  const [trail, setTrail] = useState([])
  const [entries, setEntries] = useState([])
  const mode = forceMode || modeFor(width)

  useEffect(() => {
    let frame = null
    const onResize = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        setWidth(window.innerWidth)
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // a gaveta não faz sentido no modo barra lateral
  useEffect(() => {
    if (mode === 'sidebar') setDrawerOpen(false)
  }, [mode])

  const setCollapsed = useCallback((value) => {
    setCollapsedState(value)
    try {
      window.localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0')
    } catch (e) {
      // ignora
    }
    // gráficos e o quadro do Tempo Real só recalculam a largura no "resize" da janela
    window.setTimeout(() => window.dispatchEvent(new Event('resize')), 260)
  }, [])

  const toggle = useCallback(() => {
    if (mode === 'sidebar') setCollapsed(!collapsed)
    else setDrawerOpen((open) => !open)
  }, [mode, collapsed, setCollapsed])

  const openDrawer = useCallback(() => setDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])

  // Ctrl+K / ⌘+K: no computador e no tablet foca a busca da barra do topo; no celular abre a gaveta
  useEffect(() => {
    const onKey = (event) => {
      if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault()
        if (mode === 'mobile') setDrawerOpen(true)
        setFocusSearch((value) => value + 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, collapsed, setCollapsed])

  const value = useMemo(
    () => ({
      mode,
      collapsed: mode === 'sidebar' && collapsed,
      drawerOpen,
      toggle,
      openDrawer,
      closeDrawer,
      setCollapsed,
      focusSearch,
      trail,
      setTrail,
      entries,
      setEntries,
    }),
    [
      mode,
      collapsed,
      drawerOpen,
      toggle,
      openDrawer,
      closeDrawer,
      setCollapsed,
      focusSearch,
      trail,
      entries,
    ]
  )

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export const useNavigation = () => useContext(NavigationContext)
